// src/lib/demoSignin.js
//
// The two things the surfox.ai sign-in page does for a prospect-demo visitor who comes back:
//
//   requestNewLink(email)   "Email me a new link": asks the server to email a fresh one-time link ONLY (the password is never touched) to the demo's own address,
//                           behind a Cloudflare Turnstile check.
//                           Works with no session and no /demo page open. The server answers the same way for any email (it never says whether
//                           a demo exists), and so does this function: it returns the server's wording, never a yes/no about the account.
//   demoEndedCheck(...)     the 5-day expiry, enforced AT SIGN-IN: a demo whose end date has passed (or that was disabled) is signed straight back
//                           out with a plain explanation, whichever way they got in (password or the emailed link).
//
// The password is typed into the form and sent to Supabase auth by signInUser, exactly like any account. It is never put in a URL, never
// stored by this app and never logged.

export const API_BASE = () => process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const ENDED_MESSAGE = 'This demo has ended. Questions, or ready to run SurFox AI on your real leads? Write to sales@getsurfox.com.';
export const EXPIRED_LINK_MESSAGE = 'That sign-in link was already used or has expired. Type your demo email above and press "Email me a new link", or sign in with the password from your demo email.';
export const INVALID_LOGIN_MESSAGE = 'Invalid email or password. Please check your credentials and try again.';
export const CHECK_NEEDED_MESSAGE = 'Please wait for the check above to finish, then press the button again.';
export const TURNSTILE_SITE_KEY = () => process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
export const NEW_LINK_NEEDS_EMAIL = 'Type the email you used for your demo in the Email Address box first.';
const EMAIL_RE = /^[^\s@<>]{1,64}@[^\s@<>]{1,255}\.[^\s@<>]{2,}$/;
export const looksLikeEmail = (v) => typeof v === 'string' && EMAIL_RE.test(v.trim());

/**
 * @returns {Promise<{ok:boolean, message:string}>}  ok true means "the request was accepted" (NOT that a demo exists)
 */
export async function requestNewLink(email, turnstileToken = '', fetchFn = (typeof fetch === 'function' ? fetch : null)) {
  if (!looksLikeEmail(email)) return { ok: false, message: NEW_LINK_NEEDS_EMAIL };
  if (TURNSTILE_SITE_KEY() && !turnstileToken) return { ok: false, message: CHECK_NEEDED_MESSAGE };
  try {
    const res = await fetchFn(`${API_BASE()}/public/demo-login/request-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim(), ...(turnstileToken ? { turnstile_token: turnstileToken } : {}) }),
    });
    let j = null;
    try { j = await res.json(); } catch (_) { j = null; }
    if (res.ok) return { ok: true, message: (j && j.message) || 'If that email has an active SurFox AI demo, a new sign-in link is on its way. Check your inbox and your spam or promotions folder.' };
    return { ok: false, message: (j && j.message) || 'We could not send that just now. Please try again in a minute, or write to sales@getsurfox.com.' };
  } catch (_) {
    return { ok: false, message: 'We could not reach the server. Check your connection and try again.' };
  }
}

/**
 * Has this prospect demo ended? Anything that is not a prospect demo is never affected. If the end date cannot be read the person is let
 * through (the server refuses every request from an expired demo anyway, see reviewerGuard) and the reason is logged.
 * @returns {Promise<{ended:boolean, unknown?:boolean}>}
 */
export async function demoEndedCheck(supabase, session, now = Date.now()) {
  const meta = session?.user?.app_metadata || {};
  if (meta.is_reviewer !== true || meta.reviewer_kind !== 'prospect_demo') return { ended: false };
  try {
    const { data, error } = await supabase.from('tenants').select('reviewer_expires_at, reviewer_disabled_at').eq('id', meta.tenant_id).maybeSingle();
    if (error || !data) { console.warn('[demoSignin] could not read the demo end date:', error?.message || 'no row'); return { ended: false, unknown: true }; }
    if (data.reviewer_disabled_at) return { ended: true };
    if (data.reviewer_expires_at && new Date(data.reviewer_expires_at).getTime() <= now) return { ended: true };
    return { ended: false };
  } catch (e) {
    console.warn('[demoSignin] could not read the demo end date:', e?.message);
    return { ended: false, unknown: true };
  }
}
